// Path Selection Tool — select and move entire paths/subpaths.
// Photoshop: Black arrow (A) vs Direct Selection (white arrow).
// This tool selects whole subpaths and moves them; Direct Selection edits individual points.

import { screenToDoc } from "../render/Viewport";
import { SetLayerPropertyCommand } from "../commands/SetLayerPropertyCommand";
import type { PointerSample, Tool, ToolContext } from "./Tool";
import type { ShapeLayer } from "../document/Layer";
import type { SubPath } from "../document/PathModel";

export class PathSelectionTool implements Tool {
  readonly id = "path-select";
  readonly shortcut = "A";

  private selectedSubpathIdx: number | null = null;
  private _isDragging = false;
  private dragStart: { x: number; y: number } | null = null;
  private originalSubpaths: SubPath[] | null = null;

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    const docPos = screenToDoc(ctx.viewport, s.x, s.y);
    this.dragStart = { x: docPos.x, y: docPos.y };
    this._isDragging = false;
    this.selectedSubpathIdx = null;
    this.originalSubpaths = null;

    const activeLayer = ctx.doc?.activeLayerId
      ? this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId)
      : null;

    if (!activeLayer || activeLayer.kind !== "shape") {
      ctx.requestRender();
      return;
    }

    const shape = activeLayer as ShapeLayer;
    const hit = this.hitTestSubpath(shape, docPos.x, docPos.y);

    if (hit === null) {
      ctx.requestRender();
      return;
    }

    this.selectedSubpathIdx = hit;
    this.originalSubpaths = shape.path.subpaths.map((sp) => ({
      ...sp,
      points: sp.points.map((p) => ({ ...p })),
    }));
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (this.selectedSubpathIdx === null || !this.dragStart || !this.originalSubpaths) {
      ctx.requestRender();
      return;
    }

    const docPos = screenToDoc(ctx.viewport, s.x, s.y);
    const dx = docPos.x - this.dragStart.x;
    const dy = docPos.y - this.dragStart.y;

    if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
      this._isDragging = true;
    }

    const activeLayer = ctx.doc?.activeLayerId
      ? this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId)
      : null;

    if (!activeLayer || activeLayer.kind !== "shape") return;

    const shape = activeLayer as ShapeLayer;
    const subpath = shape.path.subpaths[this.selectedSubpathIdx];
    if (!subpath) return;

    const orig = this.originalSubpaths[this.selectedSubpathIdx];
    if (!orig) return;

    // Reset to original then apply current offset
    for (let i = 0; i < subpath.points.length; i++) {
      const origPt = orig.points[i];
      const pt = subpath.points[i];
      if (!origPt || !pt) continue;
      pt.x = origPt.x + dx;
      pt.y = origPt.y + dy;
      if (pt.leftDirection && origPt.leftDirection) {
        pt.leftDirection.x = origPt.leftDirection.x + dx;
        pt.leftDirection.y = origPt.leftDirection.y + dy;
      }
      if (pt.rightDirection && origPt.rightDirection) {
        pt.rightDirection.x = origPt.rightDirection.x + dx;
        pt.rightDirection.y = origPt.rightDirection.y + dy;
      }
    }

    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext, s: PointerSample): void {
    if (this._isDragging && ctx.doc?.activeLayerId && this.selectedSubpathIdx !== null) {
      const activeLayer = this.findLayerById(ctx.doc.layers, ctx.doc.activeLayerId);
      if (activeLayer?.kind === "shape") {
        ctx.emit(new SetLayerPropertyCommand(ctx.doc.activeLayerId, {
          path: { ...activeLayer.path },
        }));
      }
    }
    this._isDragging = false;
    this.dragStart = null;
  }

  getSelectedSubpathIdx(): number | null {
    return this.selectedSubpathIdx;
  }

  clearSelection(): void {
    this.selectedSubpathIdx = null;
    this.originalSubpaths = null;
  }

  private hitTestSubpath(shape: ShapeLayer, x: number, y: number): number | null {
    const HIT_RADIUS = 8;

    for (let si = shape.path.subpaths.length - 1; si >= 0; si--) {
      const subpath = shape.path.subpaths[si]!;
      // Check if point is near any segment or point in the subpath
      for (const pt of subpath.points) {
        const dist = Math.hypot(x - pt.x, y - pt.y);
        if (dist < HIT_RADIUS) return si;
        if (pt.leftDirection) {
          const d = Math.hypot(x - pt.leftDirection.x, y - pt.leftDirection.y);
          if (d < HIT_RADIUS) return si;
        }
        if (pt.rightDirection) {
          const d = Math.hypot(x - pt.rightDirection.x, y - pt.rightDirection.y);
          if (d < HIT_RADIUS) return si;
        }
      }
    }
    return null;
  }

  private findLayerById(layers: readonly import("../document/Layer").Layer[], id: string): import("../document/Layer").Layer | null {
    for (const layer of layers) {
      if (layer.id === id) return layer;
      if (layer.kind === "group") {
        const found = this.findLayerById(layer.children, id);
        if (found) return found;
      }
    }
    return null;
  }
}
